import { Box, Divider, FormControl, IconButton, Popper } from '@mui/material';
import { useRef, useState } from 'react';
import {
  NestedFieldsSelector,
  NestedFieldsField,
} from '../../common/fieldsSelectors/NestedFieldsSelector';
import FieldLabel from '../../common/FieldLabel';
import { Plus as PlusIcon } from '@sweep-io/sweep-design/dist/icons';
import { SmileyIcon } from '../../../icons/generalIcons';
import data from '@emoji-mart/data';
import { init } from 'emoji-mart';
import Picker from '@emoji-mart/react';
import { colors } from '@sweep-io/sweep-design';
import { useSweepFieldsInfo } from '../../../sweep-fields/useSweepFieldsInfo';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.bubble.css';
import React from 'react';
import { FieldContext } from '../../../types/enums/FieldContext';
import { useFeatureToggle } from '../../../feature-flags/useFeatureToggle';
import { useTimeout } from '../../../hooks/useTimeout';
import { useCurlyBraceBlocking } from '../../../hooks/useCurlyBraceBlocking';
import { createFieldTagBlot } from '../utils/fieldTagBlotFactory';
import { createAssignmentFieldBlot } from '../utils/assignmentFieldBlotFactory';
import { AssignmentFieldMenu } from './AssignmentFieldMenu';
import {
  ASSIGNMENT_MESSAGE_OPTIONS,
  AssignmentFieldOption,
  getAssignmentFieldLabel,
} from './assignmentFieldOptions';
import { ASSIGNMENT_FIELD_WRAPPER } from '../../../types/serverImportedTypes';

interface SlackNewMessageEditorProps {
  readonly?: boolean;
  messageStruct: SlackMessageStruct;
  crmOrgId: string;
  onChange: (messageStruct: SlackMessageStruct) => any;
  objectName: string;
  label: string;
  isRequired?: boolean;
  hideEmoji?: boolean;
  infoTooltipTitle?: string;
  customOnKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => any;
  placeholder?: string;
  isHideDynamicField?: boolean;
  showAssignmentSelector?: boolean;
}

const modules = {
  toolbar: false,
};

const stripEmojis = (str: string) =>
  str.replace(
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g,
    '',
  );

const convertHtmlToValidString = (content: string) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(content, 'text/html');

  let constructedString = '';

  const loopThroughHtmlBuildString = (node: ChildNode, index: number) => {
    if (node.nodeName === '#text') {
      const data = node.nodeValue ?? '';
      // Convert &nbsp; entities back to regular spaces
      const spacesRestored = data.replace(/\u00A0/g, ' ');
      return stripEmojis(spacesRestored);
    }
    if (node.nodeName === 'EM-EMOJI') {
      return (node as Element).getAttribute('shortcodes');
    }
    if (node.nodeName === 'SPAN' && (node as Element).className === 'sfField') {
      return `{{{${(node as Element).getAttribute('chipId')}}}}`;
    }
    if (node.nodeName === 'SPAN' && (node as Element).className === 'assignmentField') {
      const fieldValue = (node as Element).getAttribute('data-field-value');
      return `${ASSIGNMENT_FIELD_WRAPPER}${fieldValue}${ASSIGNMENT_FIELD_WRAPPER}`;
    }
    if (node.nodeName === 'P' && index !== 0) {
      return '\n';
    }
    return '';
  };

  const getData = (el: ChildNode, index: number) => {
    constructedString += loopThroughHtmlBuildString(el, index);
    if (
      el.hasChildNodes() &&
      (el as Element).className !== 'sfField' &&
      (el as Element).className !== 'assignmentField' &&
      el.nodeName !== 'EM-EMOJI'
    ) {
      el.childNodes.forEach((innerEl) => {
        getData(innerEl, index);
      });
    }
  };
  document.body.childNodes.forEach((el, index) => {
    getData(el, index);
  });
  return constructedString;
};

const Embed = ReactQuill.Quill.import('blots/embed');
class EmojiBlot extends Embed {
  static create(emojis: any) {
    const node = super.create();
    node.setAttribute('shortcodes', emojis);
    return node;
  }

  static formats(node: any) {
    return node.getAttribute('shortcodes');
  }

  static value(node: any) {
    return node.getAttribute('shortcodes');
  }
}
EmojiBlot.blotName = 'emoji';
EmojiBlot.tagName = 'em-emoji';
ReactQuill.Quill.register(EmojiBlot, true);

const convertStringToValidHtml = (outerContent: string) => {
  if (!outerContent) return '';

  const getResults = (content: string) => {
    let tempString = '<p>';

    const splitArray = content.split(
      /({{{\d+}}}|:[^\s:]+(?:::skin-tone-[2-6])?:|\^{3}[^{^]+\^{3})/g,
    );

    splitArray.forEach((innerNode: string) => {
      if (innerNode.startsWith(':') && innerNode.endsWith(':')) {
        tempString += `<em-emoji shortcodes=${innerNode}></em-emoji>`;
      } else if (innerNode.startsWith('{{{')) {
        const tagNum = innerNode.match(/\d+/)?.[0];
        const elem = `<span class="sfField" chipId=${tagNum}></span>`;
        tempString += elem;
      } else if (innerNode.startsWith('^^^') && innerNode.endsWith('^^^')) {
        const fieldValue = innerNode.slice(3, -3);
        const fieldLabel = getAssignmentFieldLabel(fieldValue);
        const elem = `<span class="assignmentField" data-field-value="${fieldValue}" data-field-label="${fieldLabel}"></span>`;
        tempString += elem;
      } else {
        // Convert multiple spaces to &nbsp; entities to preserve them in HTML display
        const spacesPreserved = innerNode.replace(
          / {2,}/g,
          (match) => ' ' + '&nbsp;'.repeat(match.length - 1),
        );
        tempString += spacesPreserved;
      }
    });
    return (tempString += ' </p>');
  };

  let multilineStr = '';
  const firstSplit = outerContent.split(/\n/g);
  firstSplit.forEach((el) => {
    const splitResults = getResults(el);
    return (multilineStr += splitResults);
  });

  return multilineStr;
};

export const SlackNewMessageEditor = ({
  readonly,
  messageStruct,
  crmOrgId,
  onChange,
  objectName,
  label,
  isRequired,
  hideEmoji,
  infoTooltipTitle,
  customOnKeyDown,
  placeholder,
  isHideDynamicField,
  showAssignmentSelector,
}: SlackNewMessageEditorProps) => {
  const { setSweepTimeout } = useTimeout();
  const { priorValueSlackMessages: priorValueSlackMessagesFt, slackAssignee: slackAssigneeFt } =
    useFeatureToggle();
  const { getLabelFromIdsWithObjectType } = useSweepFieldsInfo();

  // Show assignment selector only when feature flag is on and prop is true
  const shouldShowAssignmentSelector = slackAssigneeFt && showAssignmentSelector;
  const [actionMessage, setActionMessage] = useState<SlackMessageStruct>(messageStruct);
  const [value, setValue] = useState(convertStringToValidHtml(messageStruct?.template));
  const quillRef = useRef() as React.MutableRefObject<ReactQuill>;
  const [blockApiChangeEvents, setBlockApiChangeEvents] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isEmojiVisible, setEmojiVisible] = useState(false);
  const { checkAndBlockCurlyBraces, WarningTooltip } = useCurlyBraceBlocking();
  init({ data });

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    customOnKeyDown && customOnKeyDown(event);
  };

  const onOuterBlur = () => {
    const content = quillRef.current?.unprivilegedEditor?.getHTML();
    handleBlur(content ?? '');
  };

  const handleBlur = (content: string) => {
    if (quillRef.current) {
      // Extract the fields used and update the indexes:
      const currentFields = [...(actionMessage?.variables || [])];
      const newFields: SlackFieldsStruct[] = [];
      const validContent = convertHtmlToValidString(content);
      const usedIndexFields = validContent.split(/({{{\d+}}})/g);
      const alignIndexesString = usedIndexFields.map((str) => {
        if (str.startsWith('{{{')) {
          const stringNum = str.match(/\d+/)?.[0];
          if (stringNum) {
            const newIndex = newFields.length;
            newFields[newIndex] = currentFields[parseInt(stringNum)];
            return `{{{${newIndex}}}}`;
          }
        } else {
          return str;
        }
      });
      const joinedContent = alignIndexesString.join('');

      const newMessage = {
        ...actionMessage,
        template: joinedContent || '',
        variables: newFields,
      };
      onChange(newMessage);
    }
  };

  const handleChange = (content: string, delta: any, source: string) => {
    // block first api change when value is initialized
    if (source === 'api' && blockApiChangeEvents) {
      setBlockApiChangeEvents(false);
    } else {
      if (quillRef.current) {
        // Check and block curly braces
        const blocked = checkAndBlockCurlyBraces(delta, source, quillRef);
        if (blocked) {
          return;
        }
        setValue(content);
      }
    }
  };

  const FieldTagBlot = createFieldTagBlot(quillRef, actionMessage, getLabelFromIdsWithObjectType, {
    backgroundColor: '#F4F6FF',
    textColor: '#4B4B4B',
    supportsPriorValue: true,
  });
  ReactQuill.Quill.register(FieldTagBlot, true);

  const AssignmentFieldBlot = createAssignmentFieldBlot(quillRef);
  ReactQuill.Quill.register(AssignmentFieldBlot, true);

  const [localFieldContext, setLocalFieldContext] = useState<FieldContext>(
    FieldContext.CURRENT_VALUE,
  );

  return (
    <>
      <FormControl
        sx={{
          width: '100%',
          marginBottom: '20px',
          maxWidth: '1148px',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            marginBottom: '3px',
            '& div': {
              color: colors.grey[800],
              fontSize: '12px',
              fontWeight: '400',
            },
          }}
        >
          <FieldLabel
            label={label}
            markLabelAsRequired={isRequired}
            infoTooltipTitle={infoTooltipTitle}
            showTooltip={!!infoTooltipTitle}
          />
        </Box>
        <Box
          sx={{
            background: '#FFFFFF',
            border: `1px solid ${colors.grey[300]}`,
            borderRadius: '4px',
            fontWeight: '400',
            position: 'relative',
          }}
        >
          <WarningTooltip />
          <Box
            sx={{
              background: '#FFFFFF',
              borderRadius: '4px',
              fontWeight: '400',
              '& .ql-container': {
                fontFamily: 'Inter',
                fontSize: '12px',
                '&  .emoji-mart-emoji > span': {
                  fontFamily: 'Inter !important',
                },
                '& .ql-editor ul>li:before': {
                  content: '"\\2022"',
                },
              },
              '& .ql-editor.ql-blank::before': {
                fontStyle: 'normal',
                fontSize: '14px',
              },
            }}
            onBlur={onOuterBlur}
          >
            <ReactQuill
              readOnly={readonly}
              placeholder={placeholder ?? 'e.g. great job for closing the deal! 🌈✨🐠'}
              theme="bubble"
              value={value || ''}
              onChange={handleChange}
              modules={modules}
              ref={quillRef}
              onKeyDown={onKeyDown}
            />
          </Box>
          <Box
            sx={{
              background: '#FFFFFF',
              borderRadius: '0 0 4px 4px',
              height: '40px',
              display: 'flex',
              flexDirection: 'row-reverse',
              position: 'relative',
              alignItems: 'center',
              paddingRight: '12px',
              gap: 1,
            }}
          >
            {shouldShowAssignmentSelector && (
              <Box>
                <AssignmentFieldMenu
                  options={ASSIGNMENT_MESSAGE_OPTIONS}
                  onSelect={(option: AssignmentFieldOption) => {
                    setSweepTimeout(() => {
                      const quillEditor = quillRef.current.getEditor();
                      const range = quillEditor.getSelection(true);
                      quillEditor.insertEmbed(
                        range.index,
                        'assignmentField',
                        {
                          fieldValue: option.value,
                          fieldLabel: option.label,
                        },
                        'silent',
                      );
                      quillEditor.setSelection(range.index + 1, 0, 'user');
                    }, 100);
                  }}
                  disabled={readonly}
                  buttonVariant="flat-grey"
                />
              </Box>
            )}
            {shouldShowAssignmentSelector && !isHideDynamicField && (
              <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: '12px 0' }} />
            )}
            {!isHideDynamicField && (
              <Box>
                <NestedFieldsSelector
                  readonly={readonly}
                  nestedPath={[]}
                  customButtonStartIcon={<PlusIcon size="medium" color={colors.grey[800]} />}
                  crmOrgId={crmOrgId}
                  objectType={objectName}
                  useCustomButton
                  customButtonText="Insert Dynamic field"
                  customButtonSx={{
                    borderRadius: '50px',
                    height: '32px',
                    fontSize: '14px',
                    background: 'transparent',
                    color: colors.grey[800],
                    border: 'none',
                    textTransform: 'unset',
                    p: '4px 8px',
                    '& span': {
                      fontWeight: 400,
                      fontSize: '14px',
                    },
                    '&:hover': {
                      background: colors.white,
                      color: colors.grey[900],
                    },
                  }}
                  onChange={({ fieldIds, fieldContext, fieldLabels }: NestedFieldsField) => {
                    const newVar = [...(actionMessage?.variables || [])];
                    const _fieldStruct: SlackFieldsStruct = {
                      fieldIds: fieldIds,
                    };
                    if (fieldContext) {
                      _fieldStruct.fieldContext = fieldContext;
                    }
                    newVar.push(_fieldStruct);

                    const newMessage = {
                      ...(actionMessage || {}),
                      variables: newVar,
                    };
                    setActionMessage(newMessage);
                    setLocalFieldContext(FieldContext.CURRENT_VALUE);
                    const isPriorValue = fieldContext === FieldContext.PRIOR_VALUE;
                    const fieldText = fieldLabels.join('.');
                    const fieldTitle = isPriorValue ? `Prior value of ${fieldText}` : fieldText;

                    setSweepTimeout(() => {
                      const quillEditor = quillRef.current.getEditor();
                      const range = quillEditor.getSelection(true);
                      quillEditor.insertEmbed(
                        range.index,
                        'sfField',
                        {
                          fieldNum: newVar.length - 1,
                          fieldTitle: fieldTitle,
                        },
                        'silent',
                      );
                      quillEditor.setSelection(range.index + 1, 0, 'user');
                    }, 200);
                  }}
                  fieldContext={localFieldContext}
                  displayFieldContextMenu={priorValueSlackMessagesFt}
                />
              </Box>
            )}
            {!(hideEmoji || isHideDynamicField) && (
              <Divider orientation="vertical" variant="middle" flexItem sx={{ margin: '12px 0' }} />
            )}

            {!hideEmoji && (
              <IconButton
                disabled={readonly}
                aria-label="add emoji"
                onClick={(event) => {
                  if (!anchorEl) {
                    setAnchorEl(event.currentTarget);
                  }
                  setEmojiVisible(!isEmojiVisible);
                }}
                className={'openPicker'}
              >
                <SmileyIcon className={'openPicker'} />
              </IconButton>
            )}
            <Popper
              sx={{ zIndex: 1600 }}
              open={isEmojiVisible}
              anchorEl={anchorEl}
              placement="left-end"
            >
              <Box
                sx={{
                  border: '1px solid #D9D9D9',
                  borderRadius: '10px',
                }}
              >
                <Picker
                  data={data}
                  disabled={readonly}
                  skinTonePosition={'none'}
                  previewPosition={'none'}
                  maxFrequentRows={2}
                  onClickOutside={(e: any) => {
                    const btnClasses = e.target.closest('button');
                    if (btnClasses && btnClasses.classList.contains('openPicker')) {
                      return;
                    }
                    setEmojiVisible(false);
                  }}
                  onEmojiSelect={(data: any) => {
                    const quillEditor = quillRef.current.getEditor();
                    const range = quillEditor.getSelection(true);
                    quillEditor.insertEmbed(range.index, 'emoji', data.shortcodes, 'silent');
                    quillEditor.setSelection(range.index + 1, 0, 'user');
                  }}
                />
              </Box>
            </Popper>
          </Box>
        </Box>
      </FormControl>
    </>
  );
};
